import { Link, useLocation } from "react-router";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FreelancerListItem } from "@/types/freelancer";
import { formatTimeAgo } from "@/utils/dateUtils";
import { FreelancerActionsMenu } from "../FreelancerActionsMenu";
import { FreelancerScoreBadge } from "./FreelancerScoreBadge";
import { FreelancerListKanbanStatus } from "./FreelancerListKanbanStatus";
import { formatLocation } from "./freelancerCardShared";
import { FreelancerAreasSummary } from "./FreelancerAreasSummary";
import { SearchHighlight } from "./SearchHighlight";
import { FreelancerSearchReasons } from "./FreelancerSearchReasons";
import { reasonsHiddenFromCard, type FreelancerSearchMatch } from "./searchMatch";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { cn } from "@/lib/utils";
import type { FreelancerResumoRapido } from "@/types/freelancer-painel";
import { LinkedInIcone, WhatsAppIcone } from "@/components/freelancers/contato/FreelancerContatoIcons";

import { DisponibilidadeBadge, DisponibilidadeDataHint } from "@/components/freelancers/DisponibilidadeBadge";
import { AlocacaoChip } from "@/components/freelancers/AlocacaoChip";
import type { DisponibilidadeSummary } from "@/utils/disponibilidadeUtils";

interface FreelancerListRowProps {
  freelancer: FreelancerListItem;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  vagaId?: string;
  disponibilidade?: DisponibilidadeSummary | null;
  filteredAreas?: string[];
  /** Busca livre da lista: termo a marcar e por que o perfil apareceu. */
  searchMatch?: FreelancerSearchMatch;
  resumoRapido?: FreelancerResumoRapido | null;
  /** Clique simples no nome abre o painel do candidato; cmd/ctrl-clique segue para a ficha. */
  onOpenDetails?: (id: string) => void;
}

export function FreelancerListRow({
  freelancer,
  isSelected,
  onToggleSelect,
  vagaId,
  disponibilidade,
  filteredAreas,
  searchMatch,
  resumoRapido,
  onOpenDetails,
}: FreelancerListRowProps) {
  const location = useLocation();
  const isInactive = freelancer.is_active === false;
  const loc = formatLocation(freelancer);
  const fieldTerm = searchMatch?.fieldTerm ?? "";
  const hiddenReasons = reasonsHiddenFromCard(searchMatch?.reasons ?? [], fieldTerm, loc);

  // Monta o destino como string para usar em <Link> (=> <a href> real, com
  // suporte nativo a cmd/ctrl/shift/clique-do-meio para abrir em nova aba).
  const buildHref = () => {
    const params = new URLSearchParams(location.search);
    const search = new URLSearchParams();
    if (vagaId) {
      search.set("returnTo", "selection");
      search.set("vaga", vagaId);
    }
    const page = params.get("page");
    if (page) search.set("page", page);
    const qs = search.toString();
    return `/freelancers/${freelancer.id}${qs ? `?${qs}` : ""}`;
  };
  const href = buildHref();

  return (
    <tr
      data-freelancer-id={freelancer.id}
      className={cn(
        "border-b hover:bg-muted/50 transition-colors",
        isInactive && "opacity-60",
        isSelected && "bg-primary/5",
      )}
    >
      <td className="px-3 py-2 align-middle">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(freelancer.id)}
          aria-label={`Selecionar ${freelancer.nome}`}
        />
      </td>
      <td className="px-3 py-2 align-middle">
        <Link
          to={href}
          data-testid="abrir-candidato"
          onClick={(event) => {
            if (!onOpenDetails || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            onOpenDetails(freelancer.id);
          }}
          className="flex items-center gap-2 text-left hover:underline min-w-0"
        >
          <span className="font-medium truncate">
            <SearchHighlight text={freelancer.nome} term={searchMatch?.nameTerm} />
          </span>
          <DisponibilidadeBadge summary={disponibilidade} level="dot" />
          <DisponibilidadeDataHint summary={disponibilidade} />
          <AlocacaoChip summary={disponibilidade} />
          {freelancer.quality_seal && (
            <BadgeCheck className="h-4 w-4 text-blue-500 fill-blue-500 shrink-0" />
          )}
          {isInactive && (
            <Badge
              variant="outline"
              className="shrink-0 border-red-200 bg-red-50 px-1.5 py-0 text-[10px] text-red-600"
            >
              inativo
            </Badge>
          )}
        </Link>
        <FreelancerSearchReasons
          reasons={hiddenReasons}
          term={fieldTerm}
          typedTerm={searchMatch?.nameTerm ?? ""}
          maxSnippets={1}
          clampClassName="line-clamp-2"
          className="mt-1 max-w-md whitespace-normal"
        />
      </td>
      {vagaId && (
        <td className="px-3 py-2 align-middle">
          <FreelancerListKanbanStatus
            vagaId={vagaId}
            freelancerId={freelancer.id}
            freelancerNome={freelancer.nome}
            freelancerEmail={freelancer.email}
          />
        </td>
      )}
      <td className="px-3 py-2 align-middle text-xs text-muted-foreground">
        <div className="flex max-w-[200px]">
          {freelancer.areas?.length ? (
            <FreelancerAreasSummary
              areas={freelancer.areas}
              filteredAreas={filteredAreas}
              searchTerm={fieldTerm}
              maxVisible={2}
            />
          ) : (
            "—"
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-middle text-xs">{loc ? <SearchHighlight text={loc} term={fieldTerm} /> : "—"}</td>
      <td className="px-3 py-2 align-middle">
        <FreelancerScoreBadge freelancer={freelancer} size="sm" />
      </td>
      <td className="px-3 py-2 align-middle text-xs">
        {typeof freelancer.media_avaliacao_perfil === "number" ? (
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
            {freelancer.media_avaliacao_perfil.toFixed(1)}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle text-xs">
        {typeof freelancer.media_avaliacao_projeto === "number" ? (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            {freelancer.media_avaliacao_projeto.toFixed(1)}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle text-xs text-center">
        {freelancer.total_aprovacoes ?? 0}
      </td>
      <td className="px-3 py-2 align-middle text-xs text-center">
        {freelancer.total_shortlists ?? 0}
      </td>
      <td className="px-3 py-2 align-middle text-xs text-muted-foreground whitespace-nowrap">
        {freelancer.last_submitted_at
          ? formatTimeAgo(freelancer.last_submitted_at).text
          : "Sem cadastro"}
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex items-center gap-0.5 justify-end">
          <WhatsAppIcone resumo={resumoRapido?.whatsapp} telefone={freelancer.telefone} />
          <LinkedInIcone freelancerId={freelancer.id} url={freelancer.linkedin_url} resumo={resumoRapido?.linkedin} iconClassName="h-3.5 w-3.5" />
          <ExternalLinkButton
            kind="portfolio"
            url={freelancer.portfolio_url}
            iconClassName="h-3.5 w-3.5"
          />
          <Button asChild variant="ghost" size="icon" className="h-7 w-7">
            <Link to={href} title="Ver ficha completa" aria-label="Ver ficha completa">
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <FreelancerActionsMenu
            freelancerId={freelancer.id}
            freelancerName={freelancer.nome}
            freelancerEmail={freelancer.email}
            isActive={!isInactive}
            copyData={{
              nome: freelancer.nome,
              pretensao_salarial: freelancer.pretensao_salarial,
              areas: freelancer.areas,
              cidade: freelancer.cidade,
              city: freelancer.city,
              state: freelancer.state,
              portfolio_url: freelancer.portfolio_url,
              linkedin_url: freelancer.linkedin_url,
            }}
          />
        </div>
      </td>
    </tr>
  );
}

interface FreelancerListTableProps {
  freelancers: FreelancerListItem[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: (checked: boolean) => void;
  vagaId?: string;
  disponibilidadeMap?: Map<string, DisponibilidadeSummary>;
  filteredAreas?: string[];
  searchMatchOf?: (freelancerId: string) => FreelancerSearchMatch | undefined;
  resumoRapidoMap?: Map<string, FreelancerResumoRapido>;
  onOpenDetails?: (id: string) => void;
}

export function FreelancerListTable({
  freelancers,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  vagaId,
  disponibilidadeMap,
  filteredAreas,
  searchMatchOf,
  resumoRapidoMap,
  onOpenDetails,
}: FreelancerListTableProps) {
  const allSelected =
    freelancers.length > 0 && freelancers.every((f) => selectedIds.has(f.id));

  return (
    <div className="border rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="border-b text-left">
            <th className="px-3 py-2 w-[42px]">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(v) => onSelectAll(!!v)}
                aria-label="Selecionar todos"
              />
            </th>
            <th className="px-3 py-2 font-medium">Nome</th>
            {vagaId && (
              <th className="px-3 py-2 font-medium">Status na vaga</th>
            )}
            <th className="px-3 py-2 font-medium">Áreas</th>
            <th className="px-3 py-2 font-medium">Local</th>
            <th className="px-3 py-2 font-medium">Score</th>
            <th className="px-3 py-2 font-medium">Perfil</th>
            <th className="px-3 py-2 font-medium">Projeto</th>
            <th className="px-3 py-2 font-medium text-center">Aprov.</th>
            <th className="px-3 py-2 font-medium text-center">Shortl.</th>
            <th className="px-3 py-2 font-medium">Atualizado</th>
            <th className="px-3 py-2 w-[150px]" />
          </tr>
        </thead>
        <tbody>
          {freelancers.map((f) => (
            <FreelancerListRow
              key={f.id}
              freelancer={f}
              isSelected={selectedIds.has(f.id)}
              onToggleSelect={onToggleSelect}
              vagaId={vagaId}
              disponibilidade={disponibilidadeMap?.get(f.id) ?? null}
              filteredAreas={filteredAreas}
              searchMatch={searchMatchOf?.(f.id)}
              resumoRapido={resumoRapidoMap?.get(f.id) ?? null}
              onOpenDetails={onOpenDetails}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
